import React, { useState } from 'react';
import classNames from '../../../../utility/classNames';
import { DropdownButtonComponent as DropdownButton } from '../../../../components';
import ProjectionRow from './ProjectionRow';
import FormUIOptions from '../../../../components/formBuilder/formElements/formUIOptions';
import SectionTitle from './SectionTitle';
import classes from 'classnames';
import useWindowSize from '../../../../hooks/useWindowSize';
import Tooltip from '../../../../components/Tooltip';
import { Switch } from '@headlessui/react';
import writeXlsxFile from 'write-excel-file';
import { isValue } from '../../../../utility/isValue';
import { formatValue } from '../../../../components/formBuilder/utility/renderValue';
import toast from 'react-hot-toast';
import getCurrentDateFormatted from '../../../../utility/dates';

export default function ProjectionTable({
  sectionSchema,
  data,
  label = 'Projections Summary',
  className,
  rehabYears = 0,
  propertyData,
}) {
  const { sectionLabel, mainField, schema, layoutClasses } = sectionSchema;
  // If there is a mainField property, implement the dropdown
  const [isDropdownOpen, setDropdownOpen] = useState(!mainField);
  const { width } = useWindowSize();

  const cols = classes(`grid sm:gap-2 xl:gap-3 min-w-fit `, {
    'grid-cols-5-projections sm:grid-cols-6': data.length === 5,
    'grid-cols-10 sm:grid-cols-11': data.length === 10,
    'grid-cols-15 sm:grid-cols-16': data.length === 15,
    'grid-cols-30 sm:grid-cols-31': data.length === 30,
    'grid-cols-50 sm:grid-cols-51': data.length === 50,
  });

  const mainCols = classes(
    `grid pt-2 font-semibold sm:gap-4 lg:gap-2 xl:gap-3 pb-2 sm:gap-4 lg:gap-2 xl:gap-3`,
    {
      'grid-cols-5-projections sm:grid-cols-7': data.length === 5,
      'grid-cols-10 sm:grid-cols-12': data.length === 10,
      'grid-cols-15 sm:grid-cols-17': data.length === 15,
      'grid-cols-30 sm:grid-cols-32': data.length === 30,
      'grid-cols-50 sm:grid-cols-52': data.length === 50,
    }
  );

  const labelClasses = classes(
    'grid -mt-2 sm:mt-0 py-2 sm:py-0 pl-4 sm:pl-0 w-full absolute sm:sticky left-0 bg-white z-20',
    {
      'col-span-5': data.length === 5,
      'col-span-10': data.length === 10,
      'col-span-15': data.length === 15,
      'col-span-30': data.length === 30,
      'col-span-50': data.length === 50,
    }
  );

  const renderMainField = () => {
    if (isDropdownOpen) return;

    return (
      mainField && (
        <div className="mt-3 font-semibold text-md">
          <div className="overflow-x-auto max-w-full mx-4 sm:mx-0">
            <div className={classNames(mainCols, 'px-2 sm:px-0')}>
              <div
                className={classNames(
                  'hidden sm:block sm:col-span-2 bg-transparent sm:bg-white'
                )}
              >
                <p></p>
              </div>
              {data.map((year) => (
                <div key={year.yearName} className={classNames('text-center')}>
                  <p className="text-neutral-600 font-light">{year.yearName}</p>
                </div>
              ))}
            </div>
            <ProjectionRow
              key={mainField.name}
              data={data}
              field={mainField}
              isDropdownOpen={isDropdownOpen}
              cols={mainCols}
              labelClasses={labelClasses}
              width={width}
              mainField
            />
          </div>
        </div>
      )
    );
  };

  const exportToExcel = () => {
    const HEADER_ROW = [
      {
        value: '',
      },
      ...data.map((year) => ({
        value: year.yearName,
        fontWeight: 'bold',
      })),
    ];

    const columns = [
      {
        width: 25,
      },

      ...data.map((_) => ({
        width: 20,
      })),
    ];

    const dataExcel = schema.map((field) => {
      //   let [sign, _] = field.formula('');
      let sign = undefined;

      if (field.formula) {
        [sign, _] = field.formula('');
      }

      let isTotal = field.total === true;

      const formattedData = [
        {
          value: field.type === 'Header' ? field.label : `    ${field.label}`,
          fontWeight:
            field.type === 'Header' || field.emphasize ? 'bold' : 'normal',
          height: 15,
        },
        ...data.map((yearData) => {
          const value = isValue(yearData[field.name])
            ? yearData[field.name]
            : yearData[field.altName];

          const secondFormat = formatValue(field, yearData);

          let color = '#000000';
          if (field.dynamic) {
            if (value < 0) {
              color = '#AC4032';
            } else if (value > 0) {
              color = '#17803D';
            }
          }
          return {
            value: sign ? `${sign}${secondFormat}` : secondFormat,
            fontWeight: field.emphasize === true ? 'bold' : 'normal',
            topBorderColor: isTotal ? '#000000' : undefined,
            color: color,
            align: 'right',
          };
        }),
      ];
      return formattedData;
    });

    const dataTest = [HEADER_ROW, ...dataExcel];
    try {
      const fileName = `${
        propertyData.propertyName
      } - ${sectionLabel} - ${getCurrentDateFormatted()}`;
      writeXlsxFile(dataTest, {
        fileName: `${fileName}.xlsx`,
        columns,
      }).then(() => {
        toast.success('Projections downloaded', { position: 'bottom-right' });
      });
    } catch (error) {
      console.log('Error downloading projection', error);
      toast.error('Error downloading projection', { position: 'bottom-right' });
    }
  };

  return (
    <section className="rounded-lg relative bg-white">
      <div className="py-4 px-6 border-b border-neutral-100 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="text-lg font-medium font-serif text-neutral-900">
            {label}
          </h3>
        </div>

        {mainField && (
          <div className="flex items-center gap-6">
            <Tooltip
              delay={true}
              text="Download the current projection in XLSX format."
            >
              <button
                onClick={exportToExcel}
                type="button"
                className="rounded-md bg-white px-2 py-1 text-sm font-semibold text-neutral-900 ring-1 ring-inset ring-neutral-200 hover:bg-gray-50"
              >
                Export to Excel
              </button>
            </Tooltip>
            <div className="flex items-center gap-1.5">
              <span className="text-neutral-500 button-content">
                Full breakdown
              </span>

              <Switch
                checked={isDropdownOpen}
                onChange={setDropdownOpen}
                className={`${
                  isDropdownOpen ? 'bg-primary-500' : 'bg-gray-200'
                } relative inline-flex h-4 w-8 items-center rounded-full`}
              >
                <span className="sr-only">Toggle Full breakdown</span>
                <span
                  className={`${
                    isDropdownOpen ? 'translate-x-4' : 'translate-x-1'
                  } inline-block h-3 w-3 transform rounded-full bg-white transition`}
                />
              </Switch>
            </div>
          </div>
        )}
      </div>
      <div className="p-6 pb-8">
        <div className={classNames(layoutClasses)}>
          <div
            className={classNames(
              'w-full text-md',
              mainField || isDropdownOpen ? 'items-start' : 'items-center'
            )}
          >
            <div className="flex-grow items-start space-y-2 w-full">
              {renderMainField()}

              {isDropdownOpen && (
                <div className="overflow-x-auto max-w-full pb-4 mx-4 sm:mx-0">
                  {rehabYears > 0 && (
                    <div
                      className={classNames(
                        `grid sm:gap-4 lg:gap-2 xl:gap-3 py-2 px-2 sm:px-0 font-semibold min-w-fit sticky top-0 sm:grid bg-white z-20`,
                        cols
                      )}
                    >
                      <div className="col-span-1 hidden sm:block"></div>
                      <div
                        className={
                          'border-b-2 border-neutral-400 col-span-' + rehabYears
                        }
                      >
                        Stabilization{' '}
                        <Tooltip
                          type="info"
                          text="The time it takes to complete the rehab and stabilize the property."
                          offset="sm:top-24 -top-4"
                          classes=""
                        />
                      </div>
                      <div
                        className={classNames(
                          'border-b-2 border-primary-200',
                          'col-span-1'
                        )}
                      >
                        Post-Rehab Year 1
                      </div>
                    </div>
                  )}

                  <div
                    className={classNames(
                      `grid sm:gap-4 lg:gap-2 xl:gap-3 pb-4 pt-5 px-2 sm:px-0 font-semibold min-w-fit sticky top-0 sm:grid bg-white z-20`,
                      cols
                    )}
                  >
                    <div className="hidden sm:block h-full w-full z-20 sm:sticky sm:left-0 bg-white">
                      <p></p>
                    </div>

                    {data.map((year, index) => (
                      <div key={year.year + index}>
                        <p className="text-end ml-1 z-20 bg-white">
                          {year.yearName}
                        </p>
                      </div>
                    ))}
                  </div>

                  {schema.map((field, index) => {
                    if (field.type === FormUIOptions.Header) {
                      return (
                        <SectionTitle
                          key={field.label + index}
                          title={field.label}
                        />
                      );
                    }
                    return (
                      <ProjectionRow
                        key={field.label}
                        data={data}
                        field={field}
                        isDropdownOpen={isDropdownOpen}
                        cols={cols}
                        labelClasses={labelClasses}
                        width={width}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
